import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { PATH } from '@app/constants';
import { OTP_ACTION } from '@app/models';
import { BiometricService, GpsV2Service, ProjectsService, UserService } from '@app/services';
import { AnalyticsService } from '@app/services/analytics/analytics.service';
import { ModalService } from '@app/services/modal/modal.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, forkJoin, map, of, Subscription, switchMap, take } from 'rxjs';
import { SingularService } from 'src/infra/analytics/singular/singular.service';
import { TikTokService } from 'src/infra/analytics/tiktok/tiktok.service';

declare var cordova: any;

@Component({
  selector: 'mnf-pi-v2-purchase-confirm',
  standalone: true,
  imports: [CommonModule, MatDialogModule],
  templateUrl: './pi-v2-purchase-confirm.component.html',
  styleUrl: './pi-v2-purchase-confirm.component.scss'
})
export class PiV2PurchaseConfirmComponent implements OnInit {

  public readonly urlBaseS3 = 'https://static.monific.com/assets/projects-images/';

  platformType: 'ios' | 'android' | 'web' = 'web';

  sub: Subscription;
  projectData: any;
  infoFunctionAutomated: any;
  modalOTPConf: any;
  otpMethodActual: any;

  isNativePlatform: boolean = false;

  isFirstInvest: boolean = false;
  isSecondInvest: boolean = false;

  informativeTextPurchase: string = 'Saldo restante después de ésta compra $';
  informativeTextBalanceAfterPurchase = 0;

  constructor(
    public dialogRef: MatDialogRef<PiV2PurchaseConfirmComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly analyticsService: AnalyticsService,
    private readonly gpsServ: GpsV2Service,
    private readonly modalServ: ModalService,
    private readonly router: Router,
    private readonly projectServ: ProjectsService,
    private readonly userServ: UserService,
    private readonly toastServ: ToastrService,
    private readonly singularService: SingularService,
    private readonly biometricService: BiometricService,
  )
  {

    this.projectData = data;
      if (data?.balance < data?.totalInvest) {
         this.informativeTextPurchase = 'Saldo faltante para completar la compra $';
         this.informativeTextBalanceAfterPurchase = data?.totalInvest - data?.balance;
      } else {
         this.informativeTextBalanceAfterPurchase = data?.balance - data?.totalInvest;
      }
    this.platformType = typeof cordova !== 'undefined' ? 'ios' : 'web';
  }

  ngOnInit(): void {

   this.checkUserParticipations(true).subscribe();

    this.isNativePlatform = this.platformType === 'ios' || this.platformType === 'android';

    this.userServ.getUserInf(true).subscribe(r => {
      if(r?.stepRegister <= 4) {
        this.dialogRef.close();
        this.toastServ.warning('Sin registro, no hay rendimientos. Completa el registro y mira cómo el dinero sí se mueve. 💃💸');
      }

      this.otpMethodActual = r.otpMethod === 'sms' ? 'SMS' : 'a tu correo electrónico';
      this.modalOTPConf = {
        header: 'Compra de participaciones',
        title: 'Verificiación de compra',
        message: `Ingresa el <span class="cl-primary-7">Token</span> que ha sido enviado ${this.otpMethodActual}`
      }
    });

    this.getUserInfo(this.projectData.pid);
  }

  buildGalleryImageUrl(imagePath: string): string {
    let basePath = this.projectData?.baseProject || this.projectData.pid;
    let fullUrl = this.urlBaseS3 + basePath + '/' + imagePath;

    let urlParts = fullUrl.split("https://static.monific.com/assets/");
    if (urlParts.length > 2) {
      fullUrl = "https://static.monific.com/assets/" + urlParts.pop();
    }

    return fullUrl;
  }

  next() {
    if (this.projectData?.amount) {
      if (this.projectData.amount <= this.projectData.partsProjectFree &&this.projectData.amount <= this.projectData.totalFreePerUser) {
        this.analyticsService.pushEvent('likely_purchase_participation', {project: this.projectData.name,amount: this.projectData.amount,total: this.projectData.totalInvest,status: 'OPEN_MODAL_BUTTON'});
  
        const coords = this.gpsServ.getCoordinatesFromLocalStorage();
  
        if (coords) {
          this.confirmPurchase();
        } else {
          this.sub = this.gpsServ.defineGPS().subscribe({
            next: (defined) => {
              if (defined) {
                this.confirmPurchase();
              } else {
                this.toastServ.error('No se pudo obtener la ubicación. Intenta nuevamente.');
              }
            }
          });
        }
      }
    }
  }
  

  getUserInfo(pid: string, reloadUserInfo = false) {
    forkJoin({
      user: this.userServ.getUserInf(reloadUserInfo),
      dev: this.projectServ.getProjectInfoToPurchase(pid),
      infoStatus: this.projectServ.getStatusMovements()
    }).subscribe(res => {
      this.projectData.partsProjectFree = res.dev?.free;
      this.projectData.balance = res?.user?.wallet?.balance;
      this.infoFunctionAutomated = res.infoStatus.info;
    })
  }

  confirmPurchase() {
    this.analyticsService.pushEvent('likely_purchase_participation', { project: this.projectData.name, amount: this.projectData.amount, total: this.projectData.totalInvest, status: 'ACCEPTED' });
    if(this.projectData.balance >= this.projectData?.totalInvest) {
      // Si tiene biometeria activa, se realiza la compra
      if(this.isNativePlatform) {
        this.purchaseWithBiometric();
      } else if (this.userServ.otpMethod === 'auth-app') {
        this.openMFA();
      } else {
        // Si no, manda otp para compra de participaciones
        this.modalServ.sendAndOpenModalOTP(OTP_ACTION.BUY, this.modalOTPConf).subscribe(otp =>
          this.purchaseModalOption(otp, this.projectData?.amount, this.projectData?.partValue));
      }
    }
    else {
      this.analyticsService.pushEvent('likely_purchase_participation', { project: this.projectData.name, amount: this.projectData.amount, total: this.projectData.totalInvest, status: 'INSUFFICIENT_BALANCE' });
      const balanceNeeded = this.projectData.totalInvest - this.projectData.balance;
      const modal = this.modalServ.openModalEnoughBalance(balanceNeeded, this.projectData.amount, this.projectData.name);

      modal.afterClosed().subscribe(res => {
        if(res) {
          this.getUserInfo(this.projectData.pid, true);
        }
      })

    }
  }

  /** Opciones para respuesta de OTP */
  private purchaseModalOption(otp: string, amount: number, value: number) {
    if (otp) {
       this.analyticsService.pushEvent('likely_purchase_participation', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest, status: 'OPEN_WITH_OTP' });
       // Si se ingresó 5 digitos del token sigue la comprobación del token para la operación
       this.dialogRef.close();
       this.purchase(amount, value, otp);
    }
  }

  /** Abrir modal para ingresar código de MultiFactor de Autenticación | Authenticator */
  private openMFA() {
    const mfa = this.modalServ.openMFA();
    this.analyticsService.pushEvent('likely_purchase_participation', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest, status: 'OPEN_MFA' });
    mfa.beforeClosed().subscribe(token => {
       if (token) { this.dialogRef.close(); this.purchase(this.projectData?.amount, this.projectData?.partValue, token, true) };
    });
  }

  /** Función para comprar participaciones con biometria-web */
  async purchaseWithBiometric() {
    const proceedWithPurchase = async () => {
      await this.purchaseByBiometric(this.projectData?.amount);
    };
  
    const fallbackToOtp = () => {
      if (this.userServ.otpMethod === 'auth-app') {
        this.openMFA();
      } else {
        this.modalServ.sendAndOpenModalOTP(OTP_ACTION.BUY, this.modalOTPConf).subscribe(otp => {
          this.purchaseModalOption(otp, this.projectData?.amount, this.projectData?.partValue);
        });
      }
    };
  
    try {
      const isAuthenticated = await this.biometricService.validateBiometrics();
      isAuthenticated ? await proceedWithPurchase() : fallbackToOtp();
    } catch (error) {
      fallbackToOtp();
    }
  }

  /** Acción para compra de participaciones */
  private purchase(amount: number, value: number, otp: string, mfa?: boolean) {
    this.projectServ.purchaseParticipations({
      amount, pid: this.projectData?.pid, otp, mfa
    })
    .pipe(
      take(1),
      switchMap((res) => {
        return this.checkUserParticipations(res);
      }),
      switchMap(result => {
        if (result?.status === 'success') {
          return this.singularService.registerInternalEvent('__INVERSION_EXITOSA__', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest })
        } else {
          return of(result);
        }
      }),
    )
    .subscribe({
       next: async () => {
            // this.tiktokServ.completePayment({
            //    contents: [{
            //      contentID: this.projectData?.pid,  // Asegúrate de siempre incluir un content_id válido
            //      contentType: TikTokContentType.PRODUCT,
            //      contentName: 'PROYECTO',
            //      quantity: this.projectData?.amount,
            //      price: this.projectData?.partValue
            //    }],
            //    value: this.projectData?.totalInvest,
            //  }).pipe(
            //    take(1),
            //    catchError(err => {
            //        console.error('Error al registrar evento de compra en TikTok', err);
            //        return EMPTY;
            //    })
            //  ).subscribe(() => {
            //  });
            this.analyticsService.pushEvent('__INVERSION_EXITOSA__', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest })
            await this.singularService.sendEvent('__INVERSION_EXITOSA__', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest });
            this.singularService.revenue('__INVERSION_EXITOSA__', 'MXN', this.projectData?.totalInvest, { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest });

            if (this.isFirstInvest) {
                await this.singularService.sendEvent('__PRIMERA_INVERSION__', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest });
                this.analyticsService.pushEvent('__PRIMERA_INVERSION__', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest, status: 'SUCCESS' });
            } else if (this.isSecondInvest) {
              await this.singularService.sendEvent('__SEGUNDA_INVERSION__', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest });
                this.analyticsService.pushEvent('__SEGUNDA_INVERSION__', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest, status: 'SUCCESS' });
            }

            this.analyticsService.pushEvent('investment_made', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest, value: this.projectData?.totalInvest, currency: 'MXN'  });

            setTimeout(() => {
              const formattedAmount = new Intl.NumberFormat('es-MX').format(this.projectData?.totalInvest || 0);

              const m = this.modalServ.modalCustom({
                img: 'https://static.monific.com/assets/assets-app/img/icon/img/purchase_success.svg',
                title: '¡Felicidades!',
                message: `Eres dueño de ${amount} ${amount === 1 ? 'participación' : 'participaciones'} de la propiedad ${this.projectData?.name}
              por un total de $${(formattedAmount)}.`,
                buttonText: 'Continuar'
              });
              m.beforeClosed().subscribe(() => this.router.navigate([PATH.ROUTE_DASHBOARD_HOME]))
            }, 300);
       },
       error: err => {
          if (mfa) {
             this.analyticsService.pushEvent('likely_purchase_participation', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest, status: 'OPEN_WITH_MFA' });
             this.openMFA();
          } else {
             this.analyticsService.pushEvent('likely_purchase_participation', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest, status: 'RETRY_MODAL_OTP' });
             // Se vuelve a abrir el modal de retiro
             this.modalServ.openModalOTP(this.modalOTPConf).subscribe(otp => this.purchaseModalOption(otp, amount, value));
          }
       }
    });
  }

  private checkUserParticipations(res: any) {
      return this.projectServ.getUserParticipationsAmount().pipe(
         take(1),
         map(res => {
            this.isFirstInvest = res.TOTAL_USER_PARTS === 0;
            this.isSecondInvest = res.TOTAL_USER_PARTS === 1;
         }),
         switchMap(() => {
            return of(res);
         }),
         catchError(err => {
            return of(res);
         })
      )
  }

  private async purchaseByBiometric(amount: number) {
    this.analyticsService.pushEvent('likely_purchase_participation', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest, status: 'PURCHASE_WITH_MONIP' });
    this.projectServ.purchasePartsByBiometric(amount, this.projectData.pid).subscribe({
      next: async () => {

         await this.singularService.sendEvent('__INVERSION_EXITOSA__', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest });

         if (this.isFirstInvest) {
            await this.singularService.sendEvent('__PRIMERA_INVERSION__', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest });
            this.analyticsService.pushEvent('__PRIMERA_INVERSION__', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest, status: 'SUCCESS' });
         } else if (this.isSecondInvest) {
            await this.singularService.sendEvent('__SEGUNDA_INVERSION__', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest });
            this.analyticsService.pushEvent('__SEGUNDA_INVERSION__', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest, status: 'SUCCESS' });
         }
         this.analyticsService.pushEvent('investment_made', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest, value: this.projectData?.totalInvest, currency: 'MXN'  });

         this.singularService.registerInternalEvent('__INVERSION_EXITOSA__', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest }).subscribe();

        this.dialogRef.close();

        const formattedAmount = new Intl.NumberFormat('es-MX').format(this.projectData?.totalInvest || 0);
        const m = this.modalServ.modalCustom({
           img: 'https://static.monific.com/assets/assets-app/img/icon/img/purchase_success.svg',
           title: '¡Felicidades!',
           message: `Eres dueño de ${amount} ${amount === 1 ? 'participación' : 'participaciones'} de la propiedad ${this.projectData?.name}
         por un total de $${(formattedAmount)}.`,
           buttonText: 'Continuar'
        });
        m.beforeClosed().subscribe(() => this.router.navigate([PATH.ROUTE_DASHBOARD_HOME]))
      },
      error: err => {
        this.analyticsService.pushEvent('purchase_participation', { project: this.projectData?.name, amount: this.projectData?.amount, total: this.projectData?.totalInvest, status: 'ERROR' });
        console.log(err);
      }
    })
  }

  close(): void {
    this.dialogRef.close();
    this.toastServ.info('Compra cancelada', 'Aviso');
  }

}
